import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTierUpgradeRequests, reviewTierUpgrade } from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

export default function TierUpgradeRequests() {
  const dispatch = useDispatch();
  const requests = useSelector((s) => s.profile.tierUpgradeRequests || []);
  const [status, setStatus] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState("");

  useEffect(() => { dispatch(getTierUpgradeRequests(status)); }, [dispatch, status]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const handleApprove = async (userId) => {
    setBusyId(userId);
    const res = await dispatch(reviewTierUpgrade(userId, "approve"));
    setBusyId(null);
    if (res?.success) dispatch(getTierUpgradeRequests(status));
    else alert(res?.message || "Unable to approve request");
  };

  const startReject = (userId) => { setRejectingId(userId); setReason(""); };
  const cancelReject = () => { setRejectingId(null); setReason(""); };

  const confirmReject = async (userId) => {
    if (!reason.trim()) return alert("Please provide a rejection reason");
    setBusyId(userId);
    const res = await dispatch(reviewTierUpgrade(userId, "reject", reason.trim()));
    setBusyId(null);
    if (res?.success) {
      setRejectingId(null);
      setReason("");
      dispatch(getTierUpgradeRequests(status));
    } else alert(res?.message || "Unable to reject request");
  };

  const docUrl = (req) => req.requestedTier === 2 ? req.idDocumentUrl : req.utilityBillUrl;
  const docLabel = (req) => req.requestedTier === 2 ? (req.idType || "ID Document") : "Proof of Address";

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h2 style={S.title}>SeerBit Tier Upgrade Requests</h2>
          <p style={S.sub}>Review and approve merchant requests to upgrade their wallet KYC tier</p>
        </div>
      </div>

      <div style={S.tabs}>
        {["pending", "approved", "rejected"].map((t) => (
          <button key={t} style={{ ...S.tab, ...(status === t ? S.tabActive : {}) }} onClick={() => setStatus(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Merchant", "Current Tier", "Requested Tier", "Document", "Submitted", "Status", ""].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 ? (
              <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: "#bbb", padding: 32 }}>No {status} tier upgrade requests</td></tr>
            ) : requests.map((r) => (
              <React.Fragment key={r._id}>
                <tr style={S.tr}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 600, color: BG, fontSize: 13 }}>{r.userId?.storeInfo?.storeName || "—"}</div>
                    <div style={{ fontSize: 11, color: "#999" }}>{r.userId?.email || "—"}</div>
                  </td>
                  <td style={S.td}>Tier {r.currentTier || 1}</td>
                  <td style={S.td}><span style={S.tierTag}>Tier {r.tierUpgradeRequest?.requestedTier}</span></td>
                  <td style={S.td}>
                    {docUrl(r.tierUpgradeRequest) ? (
                      <a href={docUrl(r.tierUpgradeRequest)} target="_blank" rel="noreferrer" style={S.docLink}>
                        <i className="bi bi-file-earmark-text" style={{ marginRight: 4 }} />{docLabel(r.tierUpgradeRequest)}
                      </a>
                    ) : "—"}
                  </td>
                  <td style={{ ...S.td, fontSize: 12, color: "#888" }}>{fmtDate(r.tierUpgradeRequest?.submittedAt)}</td>
                  <td style={S.td}>
                    <span style={{ ...S.statusPill, ...statusStyle(r.tierUpgradeRequest?.status) }}>{r.tierUpgradeRequest?.status}</span>
                    {r.tierUpgradeRequest?.status === "rejected" && r.tierUpgradeRequest?.rejectionReason && (
                      <div style={{ fontSize: 11, color: "#e63946", marginTop: 4, maxWidth: 180 }}>{r.tierUpgradeRequest.rejectionReason}</div>
                    )}
                  </td>
                  <td style={S.td}>
                    {status === "pending" && (
                      <div style={{ display: "flex", gap: 8 }}>
                        <button style={S.approveBtn} disabled={busyId === r.userId?._id} onClick={() => handleApprove(r.userId?._id)}>
                          <i className="bi bi-check-lg" style={{ marginRight: 4 }} />Approve
                        </button>
                        <button style={S.rejectBtn} disabled={busyId === r.userId?._id} onClick={() => startReject(r.userId?._id)}>
                          <i className="bi bi-x-lg" style={{ marginRight: 4 }} />Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                {rejectingId === r.userId?._id && (
                  <tr>
                    <td colSpan={7} style={{ ...S.td, background: "#fafafa" }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <input
                          style={S.reasonInput}
                          placeholder="Reason for rejection (sent to the merchant by email)…"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          autoFocus
                        />
                        <button style={S.rejectBtn} disabled={busyId === r.userId?._id} onClick={() => confirmReject(r.userId?._id)}>Confirm Reject</button>
                        <button style={S.cancelBtn} onClick={cancelReject}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const statusStyle = (status) => {
  if (status === "approved") return { background: "rgba(167,204,72,0.12)", color: "#5a8a00" };
  if (status === "rejected") return { background: "rgba(230,57,70,0.1)", color: "#e63946" };
  return { background: "rgba(244,162,97,0.15)", color: "#c1741f" };
};

const S = {
  root: { paddingBottom: 40 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 18 },
  title: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  sub: { fontSize: 13, color: "#888", marginTop: 4 },
  tabs: { display: "flex", gap: 6, marginBottom: 18 },
  tab: { padding: "7px 14px", borderRadius: 20, border: "1.5px solid #e0e0e0", background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#888" },
  tabActive: { background: PRIMARY, borderColor: PRIMARY, color: "#fff" },
  tableWrap: { background: "#fff", borderRadius: 10, overflowX: "auto", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 800 },
  th: { background: BG, color: "#fff", padding: "11px 14px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: { padding: "11px 14px", fontSize: 13, color: "#555", verticalAlign: "middle" },
  tierTag: { background: "rgba(155,93,229,0.12)", color: "#9b5de5", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 },
  statusPill: { borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, display: "inline-block" },
  docLink: { color: "#4fb0c6", fontSize: 12, fontWeight: 600, textDecoration: "none" },
  approveBtn: { background: "rgba(167,204,72,0.12)", border: "1px solid " + PRIMARY, borderRadius: 6, color: "#5a8a00", padding: "5px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  rejectBtn: { background: "rgba(230,57,70,0.08)", border: "1px solid #e63946", borderRadius: 6, color: "#e63946", padding: "5px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  cancelBtn: { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 6, color: "#888", padding: "5px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  reasonInput: { flex: 1, minWidth: 260, padding: "7px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 13, outline: "none" },
};
